import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { ArrowUpRightIcon, XIcon } from 'lucide-react';
import { newsStories } from '../data/news';
type Story = (typeof newsStories)[number];
type NewsReaderProps = {
  story: Story | null;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function NewsReader({
  story,
  onClose
}: NewsReaderProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!story) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [story, onClose]);
  return <AnimatePresence data-id="element-280" data-mp="13t98l4ddxa8c">
      {story && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/70 p-0 backdrop-blur-sm md:items-center md:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={onClose} data-id="element-281" data-mp="13vm0a98y2yyl">
          <motion.article role="dialog" aria-modal="true" aria-labelledby="reader-title" className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-[2rem] bg-sand md:rounded-[2rem]" initial={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} transition={{
        duration: 0.25,
        ease: EASE
      }} onClick={e => e.stopPropagation()} data-id="element-282" data-mp="kdq0cxfd8b19">
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close story" className="absolute right-4 top-4 z-10 rounded-full bg-sand p-2.5 text-ink transition-colors duration-150 hover:bg-sun" data-id="element-283" data-mp="3iuk5vn1gles5">
              <XIcon size={20} data-id="element-284" data-mp="1pcmmvd2vuqzi" />
            </button>
            <img src={story.image} alt="" className="aspect-[16/9] w-full object-cover" style={{
          objectPosition: story.imagePosition
        }} data-id="element-285" data-mp="1pf8rb3uh8qdi" />
            <div className="p-8 md:p-12" data-id="element-286" data-mp="1ao06d81irt6p">
              <div className="flex flex-wrap items-center gap-3 text-sm font-bold" data-id="element-287" data-mp="1ap1gnaapobnd">
                <time dateTime={story.date} className="text-ink/60" data-id="element-288" data-mp="16m56m7z8lnv1">
                  {format(parseISO(story.date), 'MMMM d, yyyy')}
                </time>
                <span className="text-ink/60" data-id="element-289" data-mp="11peicxda3lyj">· {story.readTime}</span>
              </div>
              <h2 id="reader-title" className="mt-5 font-display text-4xl uppercase leading-[0.95] text-ink md:text-5xl" data-id="element-290" data-mp="wq1waeu8eix0">
                {story.title}
              </h2>
              <div className="mt-8 space-y-5 text-lg leading-relaxed text-ink/80" data-id="element-291" data-mp="2r3jk82yjthsk">
                {story.body.map((p, i) => <p key={i} data-id="element-292" data-mp="2yjoj6h2zxoii" data-mpi={i}>{p}</p>)}
              </div>
              {story.link && <a href={story.link.href} target="_blank" rel="noopener noreferrer" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 font-bold text-sand transition-[transform,background-color] duration-150 ease-out hover:bg-catracho active:scale-[0.97]" data-id="element-293" data-mp="33irf9c100kec">
                  {story.link.label}
                  <ArrowUpRightIcon size={18} className="transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-294" data-mp="2pl2ue46xld9a" />
                </a>}
              {story.gallery && story.gallery.length > 0 && <div className="mt-10 grid items-start gap-6 sm:grid-cols-2" data-id="element-295" data-mp="9tf6ey9wav2m">
                  {story.gallery.map(img => <figure key={img.src} data-id="element-296" data-mp="7g1nxirnr2g5">
                      <img src={img.src} alt={img.alt} className="w-full rounded-2xl" data-id="element-297" data-mp="3doh0r16cyhs" />
                      <figcaption className="mt-2 text-center font-script text-xl text-flamingo" data-id="element-298" data-mp="3frefw4nptwj">
                        {img.caption}
                      </figcaption>
                    </figure>)}
                </div>}
            </div>
          </motion.article>
        </motion.div>}
    </AnimatePresence>;
}